import _, { filter as _filter } from 'lodash';
import { find as _find } from 'lodash';

class FinanceSummaryController {
  constructor(authService, reportingService, enums, loadingService, printService, exportService, lookupService, $q, userService) {
    "ngInject";

    if(!authService.validateUser(false, 'Reports', enums.PERMISSION_TYPE.View, true)) { return; }

    this.authService = authService;
    this.reportingService = reportingService;
    this.enums = enums;
    this.loadingService = loadingService;
    this.printService = printService;
    this.exportService = exportService;
    this.lookupService = lookupService;
    this.$q = $q;    
    this.userService = userService;

    this.reportCriteria = {};
    this.dtPopups = {};
    this.errMsgs = [];

    this.yearLabel = '';
    this.monthLabel = '';
    
    this.panels = {
      reportCriteria: true,
      reportSummary: false,
      reportData: false
    };

    this.summary = [];

    this.initializeLookups()
      .then(() => {
        this.getUserLocations();
        this.getDateRangeList();
        this.getVehicleConditionList();
        this.reset();
      });     
  }

  convertEnumToKeyValueList(enumObj, defaultOption) {
    let objList = [];

    if (defaultOption) {
      objList.push({
        id: null,
        name: 'Please Select'
      })
    }

    for(let prop in enumObj){
      objList.push({
        id: enumObj[prop],
        name: prop
      });
    }

    return objList;
  }  

  initializeLookups() {
    const ctrl = this;
    const deferred = ctrl.$q.defer();

    this.lookupService.lookupBrands()
      .then(brands => {
        brands.splice(0, 1);
        brands.unshift({id: null, name: 'All'});
        this.brands = brands;

        this.lookupService.lookupBrandModels()
          .then(brandModels => {
            this.allBrandModels = brandModels;
            deferred.resolve();
          });
      });

      return deferred.promise;
  }

  getUserLocations() {
    const locationsWithRoles = this.authService.authData.userLocations.map(l => {      
        return {
          id: l.key, 
          name: l.value,
          roleId: _find(this.authService.authData.userRoles, o => o.entityId === l.key).roleId,
          isDefault: l.defaultLocation
        };
    });

    this.userLocations = _(locationsWithRoles)
              .filter(o => o.roleId !== 2 || o.isDefault)
              .map(o => ({id: o.id, name: o.name}))
              .value();

    this.userLocations.unshift({id: null, name: 'All'})
  }

  updateBrandModel() {
    this.brandModels = _filter(this.allBrandModels, o => o.brandId === this.reportCriteria.brand.brandId);
    this.brandModels.unshift({id: null, name: 'All'});
    this.reportCriteria.brandModel = this.brandModels[0];
  }  
  
  getVehicleConditionList() {
    this.vehicleConditionList = this.convertEnumToKeyValueList(this.enums.VEHICLE_CONDITION);
    this.vehicleConditionList.unshift({id:null, name: 'All'});
  }    

  getDateRangeList() {
    this.dateRangeList = [];
    const months = ['December', 'November', 'October', 'September', 'August', 'July', 'June', 'May', 'April', 'March', 'February', 'January'].reverse();

    const currentDate = new Date();

    for (let i=0; i <= 11; i++) {            
      this.dateRangeList.push(
        {
          id: currentDate.getFullYear() + '-' + (currentDate.getMonth() + 1), 
          name: months[currentDate.getMonth()] + ' ' + currentDate.getFullYear()
        }
      );

      currentDate.setMonth(currentDate.getMonth() - 1);
    }
  }  

  reset() {        
    this.summary = [];

    this.errMsgs = [];

    this.reportData = null;

    this.yearLabel = 'Year';
    this.monthLabel = 'Month';    

    this.reportCriteria.dateRangeCode = this.dateRangeList[0].id;
    this.reportCriteria.vehicleCondition = this.vehicleConditionList[0];
    this.reportCriteria.startDate = new Date();
    this.reportCriteria.endDate = new Date();
    this.reportCriteria.location = this.userLocations[0];
    this.reportCriteria.brand = this.brands[0];
    
    this.updateUserList();
    this.updateBrandModel();        
  }

  updateUserList() {
    this.loadingService.showLoading('reportCriteria', 'Updating Criteria ...');

    this.userService.getEntityUsers(this.getLocationIds())
      .then((response) => {
        this.userList = response.data.map((i) => {return {id: i.userId, name: i.firstName + ' ' + i.lastName};});
        this.userList.unshift({id: null, name: 'All'});

        this.reportCriteria.user = this.userList[0];
      })
      .finally(() => {
        this.loadingService.hideLoading('reportCriteria');
      });
  } 
  
  getLocationIds() {
    let locationIds;

    if (!this.reportCriteria.location.id) {
      locationIds = _filter(this.userLocations, o => o.id).map(ul => ul.id);
    }
    else {
      locationIds = [this.reportCriteria.location.id];
    }

    return locationIds;
  }  

  runReport() {
    this.panels.reportData = true;
    
    if (!this.validateCriteria()) {return;}

    this.loadingService.showLoading('reportData', 'Running Report ...');

    this.reportingService.runReport('financeSummary', this.parseCriteria())
      .then(response => {
        this.reportData = response.data ? response.data.data[0] : null;
      })
      .finally(() => {
        this.loadingService.hideLoading('reportData');
        
        const dateRange = _find(this.dateRangeList, o => o.id === this.reportCriteria.dateRangeCode);

        this.yearLabel = `Jan - ${dateRange.name}`;
        this.monthLabel = dateRange.name;
      });
  }

  validateCriteria() {
    this.errMsgs = [];

    return this.errMsgs.length === 0;
  }

  parseCriteria() {
    let parsedCriteria = {};

    parsedCriteria.dateRangeCode = this.reportCriteria.dateRangeCode;
    parsedCriteria.vehicleCondition = this.reportCriteria.vehicleCondition.id;    
    parsedCriteria.locationIds = this.getLocationIds();
    parsedCriteria.salesRepId = this.reportCriteria.user.id;
    parsedCriteria.brandId = this.reportCriteria.brand.brandId;
    parsedCriteria.brandModelId = this.reportCriteria.brandModel.brandModelId;    

    return parsedCriteria;
  }

  printReport(){
    if (!this.validateCriteria()) {
      return;
    }

    this.printService.printReport('Finance Summary', this.parseCriteria());
  }

  exportReport(){
    if (!this.validateCriteria()) {
      return;
    }

    this.exportService.exportReport('Finance Summary', this.parseCriteria());
  }  
}

export default FinanceSummaryController;



// WEBPACK FOOTER //
// ./client/app/components/reports/financeSummary/financeSummary.controller.js